<template>
  <Teleport to="body">
    <transition name="drawer-slide">
      <div v-if="modelValue" class="kanban-editor-overlay" @click.self="close">
        <div class="kanban-editor">
          <!-- Header -->
          <div class="kanban-editor__header">
            <h3 class="kanban-editor__title">
              {{ column ? 'Editar Coluna' : 'Nova Coluna' }}
            </h3>
            <button class="kanban-editor__close" @click="close">
              <i class="i-lucide-x size-4" />
            </button>
          </div>

          <!-- Tabs -->
          <div class="kanban-editor__tabs">
            <button
              v-for="tab in tabs"
              :key="tab.id"
              class="kanban-editor__tab"
              :class="{ 'kanban-editor__tab--active': activeTab === tab.id }"
              @click="activeTab = tab.id"
            >
              <i :class="[tab.icon, 'size-4 mr-1.5']" />
              {{ tab.label }}
            </button>
          </div>

          <!-- Tab: General -->
          <div v-if="activeTab === 'general'" class="kanban-editor__body">
            <div class="kanban-editor__field">
              <label class="kanban-editor__label">Nome da Coluna</label>
              <input
                v-model="form.name"
                type="text"
                placeholder="Ex: Em Atendimento"
                class="kanban-editor__input"
              />
            </div>

            <div class="kanban-editor__field">
              <label class="kanban-editor__label">Cor</label>
              <div class="kanban-editor__colors">
                <button
                  v-for="color in colorPresets"
                  :key="color"
                  class="kanban-editor__color-swatch"
                  :class="{ 'kanban-editor__color-swatch--selected': form.color === color }"
                  :style="{ backgroundColor: color }"
                  @click="form.color = color"
                />
                <input
                  v-model="form.color"
                  type="color"
                  class="kanban-editor__color-input"
                  title="Cor personalizada"
                />
              </div>
            </div>

            <div class="kanban-editor__field">
              <label class="kanban-editor__label">Limite WIP (deixe vazio para sem limite)</label>
              <input
                v-model.number="form.wipLimit"
                type="number"
                min="1"
                placeholder="Ex: 10"
                class="kanban-editor__input"
              />
            </div>

            <div class="kanban-editor__field">
              <label class="kanban-editor__label">
                Status nativo do CROWORK
                <span class="text-xs text-n-slate-9 font-normal">
                  (status real aplicado nas conversas desta coluna)
                </span>
              </label>
              <select
                v-model="form.nativeStatus"
                class="kanban-editor__select"
                :disabled="isSystem"
              >
                <option value="open">Aberto (open)</option>
                <option value="pending">Pendente (pending)</option>
                <option value="snoozed">Adiado (snoozed)</option>
                <option value="resolved">Resolvido (resolved)</option>
              </select>
              <p v-if="isSystem" class="kanban-editor__hint">
                Coluna nativa fixa — o status não pode ser alterado.
              </p>
            </div>
          </div>

          <!-- Tab: Sort -->
          <div v-if="activeTab === 'sort'" class="kanban-editor__body">
            <div class="kanban-editor__field">
              <label class="kanban-editor__label">Ordenar por</label>
              <select v-model="form.sortConfig.type" class="kanban-editor__select">
                <option value="manual">Manual (drag-and-drop)</option>
                <option value="time-without-response">Tempo sem resposta</option>
                <option value="priority">Prioridade</option>
                <option value="created-at">Data de criação</option>
                <option value="updated-at">Última atualização</option>
                <option value="unread-count">Mensagens não lidas</option>
              </select>
            </div>

            <div class="kanban-editor__field">
              <label class="kanban-editor__label">Direção</label>
              <div class="kanban-editor__radio-group">
                <label class="kanban-editor__radio">
                  <input v-model="form.sortConfig.direction" type="radio" value="asc" />
                  <span>Crescente (menor primeiro)</span>
                </label>
                <label class="kanban-editor__radio">
                  <input v-model="form.sortConfig.direction" type="radio" value="desc" />
                  <span>Decrescente (maior primeiro)</span>
                </label>
              </div>
            </div>

            <div class="kanban-editor__field">
              <label class="kanban-editor__toggle-row">
                <span>Ordenação automática</span>
                <button
                  class="kanban-editor__toggle"
                  :class="{ 'kanban-editor__toggle--on': form.sortConfig.autoSort }"
                  @click="form.sortConfig.autoSort = !form.sortConfig.autoSort"
                >
                  <span class="kanban-editor__toggle-knob" />
                </button>
              </label>
              <p class="kanban-editor__hint">Reordena os cards automaticamente ao abrir a coluna</p>
            </div>
          </div>

          <!-- Tab: Rules -->
          <div v-if="activeTab === 'rules'" class="kanban-editor__body">
            <div class="kanban-editor__rules-header">
              <p class="kanban-editor__hint">Regras executadas a cada 30s automaticamente</p>
              <button class="kanban-editor__btn-add-rule" @click="addRule">
                <i class="i-lucide-plus size-4 mr-1" />
                Adicionar Regra
              </button>
            </div>

            <div v-if="form.rules.length === 0" class="kanban-editor__empty-rules">
              <i class="i-lucide-workflow size-8 text-n-slate-7 mb-2" />
              <p>Nenhuma regra configurada</p>
            </div>

            <div
              v-for="(rule, idx) in form.rules"
              :key="rule.id"
              class="kanban-editor__rule"
            >
              <div class="kanban-editor__rule-header">
                <label class="kanban-editor__toggle-row">
                  <span class="text-xs font-medium text-n-slate-11">Regra {{ idx + 1 }}: {{ rule.name || 'Sem nome' }}</span>
                  <button
                    class="kanban-editor__toggle"
                    :class="{ 'kanban-editor__toggle--on': rule.enabled }"
                    @click="rule.enabled = !rule.enabled"
                  >
                    <span class="kanban-editor__toggle-knob" />
                  </button>
                </label>
                <button class="kanban-editor__rule-remove" @click="removeRule(idx)">
                  <i class="i-lucide-trash-2 size-3.5" />
                </button>
              </div>

              <div class="kanban-editor__rule-fields">
                <input
                  v-model="rule.name"
                  type="text"
                  placeholder="Nome da regra"
                  class="kanban-editor__input kanban-editor__input--sm"
                />

                <div class="kanban-editor__rule-row">
                  <span class="kanban-editor__rule-label">SE</span>
                  <select v-model="rule.condition" class="kanban-editor__select kanban-editor__select--sm">
                    <option value="time-in-column">Tempo na coluna (min)</option>
                    <option value="no-response">Sem resposta (min)</option>
                    <option value="priority-level">Prioridade igual a</option>
                    <option value="sla-breach">SLA violado</option>
                    <option value="keyword-match">Mensagem contém</option>
                    <option value="unread-count">Não lidas ≥</option>
                  </select>
                  <input
                    v-if="rule.condition !== 'sla-breach'"
                    v-model="rule.conditionValue"
                    type="text"
                    :placeholder="getConditionPlaceholder(rule.condition)"
                    class="kanban-editor__input kanban-editor__input--sm"
                  />
                </div>

                <div class="kanban-editor__rule-row">
                  <span class="kanban-editor__rule-label">ENTÃO</span>
                  <select v-model="rule.action" class="kanban-editor__select kanban-editor__select--sm">
                    <option value="move-to-column">Mover para coluna</option>
                    <option value="change-priority">Mudar prioridade</option>
                    <option value="add-tag">Adicionar tag</option>
                    <option value="notify">Notificar</option>
                    <option value="assign-agent">Atribuir agente</option>
                  </select>
                  <input
                    v-model="rule.actionValue"
                    type="text"
                    :placeholder="getActionPlaceholder(rule.action)"
                    class="kanban-editor__input kanban-editor__input--sm"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Tab: Agents -->
          <div v-if="activeTab === 'agents'" class="kanban-editor__body">
            <div class="kanban-editor__field">
              <label class="kanban-editor__label">Modo de Atribuição</label>
              <select v-model="form.agentConfig.assignmentMode" class="kanban-editor__select">
                <option value="none">Desativado</option>
                <option value="default">Agente padrão</option>
                <option value="round-robin">Round-robin (rotativo)</option>
              </select>
              <p class="kanban-editor__hint">
                <template v-if="form.agentConfig.assignmentMode === 'none'">Nenhuma atribuição automática</template>
                <template v-else-if="form.agentConfig.assignmentMode === 'default'">Atribui sempre ao agente selecionado quando um card entra nesta coluna</template>
                <template v-else>Distribui cards entre os agentes selecionados em ordem rotativa</template>
              </p>
            </div>

            <!-- Default agent picker -->
            <div v-if="form.agentConfig.assignmentMode === 'default'" class="kanban-editor__field">
              <label class="kanban-editor__label">Agente Padrão</label>
              <select v-model="form.agentConfig.defaultAgentId" class="kanban-editor__select">
                <option :value="null">Selecionar agente...</option>
                <option
                  v-for="agent in allAgents"
                  :key="agent.id"
                  :value="agent.id"
                >{{ agent.name }}</option>
              </select>
            </div>

            <!-- Round-robin agent pool -->
            <div v-if="form.agentConfig.assignmentMode === 'round-robin'" class="kanban-editor__field">
              <label class="kanban-editor__label">Pool de Agentes ({{ form.agentConfig.agentIds.length }} selecionados)</label>
              <div class="kanban-editor__agent-list">
                <label
                  v-for="agent in allAgents"
                  :key="agent.id"
                  class="kanban-editor__agent-row"
                >
                  <input
                    type="checkbox"
                    :checked="form.agentConfig.agentIds.includes(agent.id)"
                    @change="toggleAgentInPool(agent.id)"
                  />
                  <img
                    v-if="agent.thumbnail"
                    :src="agent.thumbnail"
                    :alt="agent.name"
                    class="w-6 h-6 rounded-full object-cover flex-shrink-0"
                  />
                  <span
                    v-else
                    class="w-6 h-6 rounded-full bg-n-slate-5 flex items-center justify-center
                           text-[10px] font-bold text-n-slate-10 flex-shrink-0"
                  >{{ agent.name?.charAt(0)?.toUpperCase() }}</span>
                  <span class="text-sm text-n-slate-11">{{ agent.name }}</span>
                </label>
                <p v-if="!allAgents?.length" class="text-xs text-n-slate-8 py-2">
                  Nenhum agente disponível
                </p>
              </div>
            </div>
          </div>

          <!-- Footer actions -->
          <div class="kanban-editor__footer">
            <button
              v-if="column && !isSystem"
              class="kanban-editor__btn-delete"
              title="Excluir coluna"
              @click="onDelete"
            >
              <i class="i-lucide-trash-2 size-4 mr-1" />
              Excluir
            </button>
            <span v-else-if="isSystem" class="kanban-editor__system-tag">
              <i class="i-lucide-lock size-3.5 mr-1" />
              Coluna nativa fixa
            </span>
            <span class="kanban-editor__footer-spacer" />
            <button class="kanban-editor__btn-cancel" @click="close">Cancelar</button>
            <button
              class="kanban-editor__btn-save"
              :disabled="!form.name.trim()"
              @click="save"
            >
              <i class="i-lucide-check size-4 mr-1" />
              Salvar
            </button>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script setup>
import { ref, reactive, watch, computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  column: { type: Object, default: null },
});

const emit = defineEmits(['update:modelValue', 'save', 'delete']);

const activeTab = ref('general');

// System columns mirror a native status 1:1 and cannot have it changed.
const isSystem = computed(() => props.column?.system === true);

// Agents from Vuex store
const allAgents = useMapGetter('agents/getAgents');

const tabs = [
  { id: 'general', label: 'Geral', icon: 'i-lucide-settings-2' },
  { id: 'sort', label: 'Ordenação', icon: 'i-lucide-arrow-up-down' },
  { id: 'rules', label: 'Regras', icon: 'i-lucide-workflow' },
  { id: 'agents', label: 'Agentes', icon: 'i-lucide-users' },
];

const colorPresets = [
  '#6366F1', '#10B981', '#F59E0B', '#6B7280',
  '#EF4444', '#F97316', '#3B82F6', '#8B5CF6',
  '#EC4899', '#14B8A6', '#84CC16', '#F43F5E',
];

const defaultForm = () => ({
  name: '',
  color: '#6366F1',
  wipLimit: null,
  nativeStatus: 'open',
  sortConfig: {
    type: 'manual',
    direction: 'asc',
    autoSort: false,
  },
  rules: [],
  agentConfig: {
    assignmentMode: 'none',
    defaultAgentId: null,
    agentIds: [],
  },
});

const form = reactive(defaultForm());

// Sync form from column prop when opening
watch(
  () => [props.modelValue, props.column],
  ([isOpen, col]) => {
    if (isOpen) {
      activeTab.value = 'general';
      if (col) {
        Object.assign(form, {
          name: col.name || '',
          color: col.color || '#6366F1',
          wipLimit: col.wipLimit || null,
          nativeStatus: col.nativeStatus || 'open',
          sortConfig: col.sortConfig
            ? { ...col.sortConfig }
            : { type: 'manual', direction: 'asc', autoSort: false },
          rules: col.rules ? col.rules.map(r => ({ ...r })) : [],
          agentConfig: col.agentConfig
            ? { ...col.agentConfig, agentIds: [...(col.agentConfig.agentIds || [])] }
            : { assignmentMode: 'none', defaultAgentId: null, agentIds: [] },
        });
      } else {
        Object.assign(form, defaultForm());
      }
    }
  },
  { immediate: false }
);

function addRule() {
  form.rules.push({
    id: `rule-${Date.now()}`,
    name: '',
    enabled: true,
    condition: 'no-response',
    conditionValue: '30',
    action: 'move-to-column',
    actionValue: '',
    priority: form.rules.length + 1,
  });
}

function removeRule(idx) {
  form.rules.splice(idx, 1);
}

function getConditionPlaceholder(condition) {
  const map = {
    'time-in-column': 'Ex: 60 (minutos)',
    'no-response': 'Ex: 30 (minutos)',
    'priority-level': 'urgent / high / medium / low',
    'keyword-match': 'Ex: urgente',
    'unread-count': 'Ex: 5',
  };
  return map[condition] || 'Valor';
}

function getActionPlaceholder(action) {
  const map = {
    'move-to-column': 'ID da coluna destino (ex: snoozed)',
    'change-priority': 'urgent / high / medium / low',
    'add-tag': 'Ex: vip',
    'notify': 'Mensagem de notificação',
    'assign-agent': 'ID ou nome do agente',
  };
  return map[action] || 'Valor';
}

function toggleAgentInPool(agentId) {
  const idx = form.agentConfig.agentIds.indexOf(agentId);
  if (idx === -1) form.agentConfig.agentIds.push(agentId);
  else form.agentConfig.agentIds.splice(idx, 1);
}

function close() {
  emit('update:modelValue', false);
}

function onDelete() {
  if (!props.column || isSystem.value) return;
  // eslint-disable-next-line no-alert
  const ok = window.confirm(
    `Excluir a coluna "${props.column.name}"? As conversas voltam para a coluna nativa do status.`
  );
  if (!ok) return;
  emit('delete', props.column);
  close();
}

function save() {
  if (!form.name.trim()) return;
  emit('save', {
    ...props.column,
    name: form.name.trim(),
    color: form.color,
    wipLimit: form.wipLimit || null,
    nativeStatus: form.nativeStatus,
    sortConfig: { ...form.sortConfig },
    rules: form.rules.map(r => ({ ...r })),
    agentConfig: {
      ...form.agentConfig,
      agentIds: [...form.agentConfig.agentIds],
    },
  });
  close();
}
</script>

<style scoped>
.kanban-editor-overlay {
  @apply fixed inset-0 z-50 flex justify-end;
  background-color: rgba(0, 0, 0, 0.4);
}

.kanban-editor {
  @apply flex flex-col bg-n-solid-1 border-l border-n-weak h-full overflow-hidden;
  width: 400px;
  max-width: 100vw;
}

.kanban-editor__header {
  @apply flex items-center justify-between px-5 py-4 border-b border-n-weak flex-shrink-0;
}
.kanban-editor__title {
  @apply text-base font-semibold text-n-slate-12;
}
.kanban-editor__close {
  @apply p-1 rounded hover:bg-n-slate-4 text-n-slate-9 hover:text-n-slate-12 transition-colors;
}

.kanban-editor__tabs {
  @apply flex border-b border-n-weak flex-shrink-0;
}
.kanban-editor__tab {
  @apply flex items-center px-4 py-2.5 text-sm text-n-slate-9 border-b-2 border-transparent
         hover:text-n-slate-12 transition-colors;
}
.kanban-editor__tab--active {
  @apply text-n-iris-9 border-n-iris-9;
}

.kanban-editor__body {
  @apply flex-1 overflow-y-auto p-5 space-y-5;
}

.kanban-editor__field {
  @apply flex flex-col gap-1.5;
}
.kanban-editor__label {
  @apply text-xs font-medium text-n-slate-10;
}
.kanban-editor__hint {
  @apply text-xs text-n-slate-9;
}
.kanban-editor__input {
  @apply px-3 py-2 text-sm bg-n-solid-2 border border-n-weak rounded-lg text-n-slate-12
         placeholder:text-n-slate-8 focus:outline-none focus:ring-1 focus:ring-n-iris-9;
}
.kanban-editor__input--sm {
  @apply py-1.5 text-xs flex-1;
}
.kanban-editor__select {
  @apply px-3 py-2 text-sm bg-n-solid-2 border border-n-weak rounded-lg text-n-slate-12
         focus:outline-none focus:ring-1 focus:ring-n-iris-9;
}
.kanban-editor__select--sm {
  @apply py-1.5 text-xs;
}

.kanban-editor__colors {
  @apply flex flex-wrap gap-2 items-center;
}
.kanban-editor__color-swatch {
  @apply w-6 h-6 rounded-full border-2 border-transparent cursor-pointer hover:scale-110 transition-transform;
}
.kanban-editor__color-swatch--selected {
  @apply border-n-slate-12 ring-2 ring-n-iris-8;
}
.kanban-editor__color-input {
  @apply w-8 h-8 rounded-full cursor-pointer border-0 p-0 overflow-hidden;
  -webkit-appearance: none;
}

.kanban-editor__radio-group {
  @apply flex flex-col gap-2;
}
.kanban-editor__radio {
  @apply flex items-center gap-2 text-sm text-n-slate-11 cursor-pointer;
}

.kanban-editor__toggle-row {
  @apply flex items-center justify-between;
}
.kanban-editor__toggle {
  @apply relative w-9 h-5 rounded-full transition-colors duration-200 flex-shrink-0;
  @apply bg-n-slate-6;
}
.kanban-editor__toggle--on {
  @apply bg-n-iris-9;
}
.kanban-editor__toggle-knob {
  @apply absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200;
}
.kanban-editor__toggle--on .kanban-editor__toggle-knob {
  transform: translateX(16px);
}

/* Rules tab */
.kanban-editor__rules-header {
  @apply flex items-center justify-between;
}
.kanban-editor__btn-add-rule {
  @apply flex items-center text-xs text-n-iris-9 hover:text-n-iris-11 font-medium;
}
.kanban-editor__empty-rules {
  @apply flex flex-col items-center py-8 text-xs text-n-slate-9;
}
.kanban-editor__rule {
  @apply border border-n-weak rounded-lg p-3 space-y-2 bg-n-solid-2;
}
.kanban-editor__rule-header {
  @apply flex items-center justify-between;
}
.kanban-editor__rule-remove {
  @apply p-0.5 rounded text-n-slate-8 hover:text-red-500 transition-colors;
}
.kanban-editor__rule-fields {
  @apply space-y-2;
}
.kanban-editor__rule-row {
  @apply flex items-center gap-1.5;
}
.kanban-editor__rule-label {
  @apply text-[10px] font-bold text-n-slate-9 w-10 flex-shrink-0;
}

/* Footer */
.kanban-editor__footer {
  @apply flex items-center justify-end gap-2 px-5 py-4 border-t border-n-weak flex-shrink-0;
}
.kanban-editor__btn-cancel {
  @apply px-4 py-2 text-sm text-n-slate-11 hover:text-n-slate-12 transition-colors;
}
.kanban-editor__btn-save {
  @apply flex items-center px-4 py-2 text-sm bg-n-iris-9 text-white rounded-lg
         hover:bg-n-iris-10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors;
}
.kanban-editor__btn-delete {
  @apply flex items-center px-3 py-2 text-sm text-n-ruby-11 hover:bg-n-ruby-3
         rounded-lg transition-colors;
}
.kanban-editor__system-tag {
  @apply flex items-center text-xs text-n-slate-10 font-medium;
}
.kanban-editor__footer-spacer {
  @apply flex-1;
}

/* Agents tab */
.kanban-editor__agent-list {
  @apply flex flex-col gap-1 max-h-56 overflow-y-auto border border-n-weak rounded-lg p-2;
}
.kanban-editor__agent-row {
  @apply flex items-center gap-2 p-1.5 rounded cursor-pointer hover:bg-n-slate-3 transition-colors;
}

/* Transition */
.drawer-slide-enter-active,
.drawer-slide-leave-active {
  transition: transform 0.2s ease;
}
.drawer-slide-enter-from,
.drawer-slide-leave-to {
  transform: translateX(100%);
}
</style>
